import React, { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import type { GenericItem, GenericItemDataSheetAttributes, GenericItemDataSheetGroups } from '@marty-js/api-sdk/types';
import { Cta } from '../../cta';
import { Badge } from '../../../atoms/badge';
import { MaterialSymbol } from '../../../atoms/material-symbol';
import { useImageComponent, useLinkComponent } from '../../../utils/component';
import { DEVICE_SIZE, mq } from '../../../utils/mq';
import { useTranslation } from '../../../utils/translation';
import { getTopSpecsFromDataSheet } from '../../../utils/datasheet';
import { NetworkIcons } from '../../../atoms/networkIcons';

interface ServiceCardProps {
  visibilty?: string;
  animated?: string;
  direction?: string;
  forceSmall?: boolean;
  onMouseEnter?: any;
  onMouseLeave?: any;
}

const Card = styled.div<ServiceCardProps>`
  display: inline-flex;
  position: relative;
  background-color: var(--theme-color-background);
  border-radius: 15px;
  height: fit-content;
  box-shadow: 0 0 30px 0 rgba(0, 0, 0, 0.1);
  flex-wrap: wrap;
  width: 100%;
  margin: var(--spacer-s);
  border: solid 1px var(--theme-palette-grey-blue);

  ${mq.lte(
    DEVICE_SIZE.LARGE,
    css`
      width: initial;
      padding-right: var(--spacer-s);
    `,
  )}

  &.mod-top {
    border: solid 3px var(--theme-palette-grey-blue);
    border-radius: 15px;
  }

  ${(props) =>
    props.forceSmall
      ? mq.gte(
          DEVICE_SIZE.LARGE,
          css<ServiceCardProps>`
            border: solid 1px var(--card-border);
            max-width: 320px;
          `,
        )
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css<ServiceCardProps>`
            border: solid 1px var(--card-border);
            margin: var(--spacer-s);
            width: 100%;
            transition: all 0.3s;

            &.mod-hover {
              box-shadow: rgb(0 0 0 / 20%) 0px 10px 30px 0px;
              transform: translate(0px, -10px);
            }
          `,
        )}
`;

const CardClickable = styled.div<ServiceCardProps>`
  bottom: 45px;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;

  &:hover ~ * h3,
  &:focus ~ * h3 {
    color: var(--theme-color-primary);
  }

  a {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
  }

  ${(props) =>
    props.forceSmall
      ? 0
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css`
            bottom: 30px;
          `,
        )}
`;

const LogoArea = styled.div<ServiceCardProps>`
  align-items: center;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  order: 0;
  padding: 25px 10px 25px 31px;
  width: 40%;

  ${(props) =>
    props.forceSmall
      ? 0
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css`
            align-self: center;
            height: auto;
            padding: 0 0 0 30px;
            width: 15%;
          `,
        )}
`;

const LeftPart = styled.div<ServiceCardProps>`
  align-self: center;
  display: flex;
  flex-direction: column;
  order: 1;
  width: 60%;

  ${(props) =>
    props.forceSmall
      ? 0
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css`
            padding: 30px 0 30px 30px;
            width: 30%;

            a {
              color: ${props.theme.isDark ? 'white' : 'initial'};
              position: relative;
              top: 0;
              width: 190%;
              padding-bottom: 10px;
            }
          `,
        )}
`;

const CenterPart = styled.div<ServiceCardProps>`
  order: 2;
  display: flex;
  align-items: center;
  flex-direction: column;
  border-top: 1px solid var(--theme-palette-dark-white);
  border-right: 1px solid var(--theme-palette-dark-white);
  border-bottom: 1px solid var(--theme-palette-dark-white);
  width: 50%;
  min-height: 107px;

  ${(props) =>
    props.forceSmall
      ? 0
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css`
            align-self: center;
            border: initial;
            height: initial;
            left: initial;
            padding: 60px 10px 10px;
            position: relative;
            top: initial;
            width: 25%;
          `,
        )}
`;

const RightPart = styled.div<ServiceCardProps>`
  align-self: center;
  order: 3;
  display: flex;
  align-items: center;
  flex-direction: column;
  border-top: 1px solid var(--theme-palette-dark-white);
  border-bottom: 1px solid var(--theme-palette-dark-white);
  width: 50%;
  min-height: 107px;
  justify-content: center;

  ${(props) =>
    props.forceSmall
      ? 0
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css`
            border: initial;
            height: initial;
            left: initial;
            width: 30%;
          `,
        )}
`;

const TopSpec = styled.div<ServiceCardProps>`
  align-items: center;
  display: none;
  line-height: 18px;
  margin-top: 10px;
  padding-left: 25px;
  position: relative;

  ${(props) =>
    props.forceSmall
      ? 0
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css`
            display: flex;
          `,
        )}
`;

const TopSpecIcon = styled(MaterialSymbol)`
  font-size: 18px;
  left: 0;
  margin: 0 12px 0 0;
  position: absolute;
`;

const FirstVerticalLine = styled.div<ServiceCardProps>`
  display: none;
  position: absolute;
  border-left: 1px solid var(--theme-palette-dark-white);
  height: calc(100% - 60px);
  top: 55px;
  width: 1px;
  left: 0;

  ${(props) =>
    props.forceSmall
      ? 0
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css`
            display: flex;
          `,
        )}
`;

const SecondVerticalLine = styled.div<ServiceCardProps>`
  display: none;
  position: absolute;
  border-left: 1px solid var(--theme-palette-dark-white);
  height: calc(100% - 30px);
  left: 70%;
  width: 1px;
  top: 0;

  ${(props) =>
    props.forceSmall
      ? 0
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css`
            display: flex;
          `,
        )}
`;

const DetailText = styled.p<ServiceCardProps>`
  display: none;
  font-size: 14px;
  margin-left: 5px;
  margin-bottom: 0;

  ${(props) =>
    props.forceSmall
      ? 0
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css`
            display: flex;
          `,
        )}
`;

const Title = styled.h3<ServiceCardProps>`
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  transition: color 0.2s;
  margin: 0;

  ${(props) =>
    props.forceSmall
      ? 0
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css`
            height: initial;
            left: initial;
            text-decoration: underline;
            text-underline-offset: 2px;
            width: initial;
          `,
        )}
`;

const InternetDataBox = styled.div<ServiceCardProps>`
  display: flex;
  flex-direction: column;
  padding-top: 10px;

  ${(props) =>
    props.forceSmall
      ? 0
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css`
            align-self: center;
            padding-top: 0;
          `,
        )}
`;

const InternetDataLabel = styled.div`
  font-size: 11px;
  line-height: 16px;
  opacity: 0.7;
  margin-top: 5px;
  text-align: center;
`;

const InternetDataText = styled.p<ServiceCardProps>`
  font-weight: 700;
  font-size: 26px;
  line-height: 42px;
  text-align: center;
  width: 145px;

  ${(props) =>
    props.forceSmall
      ? 0
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css`
            font-size: 32px;
            margin-top: 0;
            width: initial;
          `,
        )}
`;

const NetworkWithIconArea = styled.div<ServiceCardProps>`
  align-items: center;
  display: flex;
  font-size: 14px;
  gap: 9px;
  justify-content: center;
  line-height: 18px;
  margin-top: 10px;

  ${(props) =>
    props.forceSmall
      ? 0
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css`
            justify-content: space-evenly;
            margin-top: 0;
            width: initial;
            font-weight: 700;
          `,
        )}
`;

const PriceText = styled.p<ServiceCardProps>`
  text-align: center;
  font-weight: 700;
  line-height: 42px;
  color: #ff033a;
  margin-top: 10px !important;
  font-size: 26px;

  ${(props) =>
    props.forceSmall
      ? 0
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css`
            margin-top: initial !important;
            font-size: 32px;
            line-height: 52px;
          `,
        )}
`;

const DiscountPeriod = styled.p<ServiceCardProps>`
  align-items: center;
  color: var(--theme-background-contrast-text-color);
  font-size: 11px;
  justify-content: center;
  text-align: center;
  line-height: 14px;
  margin: 10px 0 15px !important;
  min-height: 28px;
  display: flex;
  padding: 0 17px;

  ${(props) =>
    props.forceSmall
      ? 0
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css`
            font-size: 11px;
            margin-top: initial !important;
            opacity: 0.7;
            padding: 0;
            width: 70%;
          `,
        )}
`;

const BadgeBox = styled.div<ServiceCardProps>`
  display: inline-block;
  left: initial;
  overflow: auto;
  position: absolute;
  right: 20px;
  top: -14px;
  width: auto;

  ${(props) =>
    props.forceSmall
      ? 0
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css`
            left: 30px;
            right: initial;
          `,
        )}
`;

const GradiantBox = styled.div<ServiceCardProps>`
  background: linear-gradient(90deg, var(--color-alt-5) 0%, var(--theme-color-background) 100%);
  border-radius: 0 0 15px 15px;
  cursor: pointer;
  display: flex;
  margin: auto;
  order: 6;
  padding: 0 0 16px 0;
  visibility: hidden;
  position: relative;
  z-index: 1;

  ${(props) =>
    props.forceSmall
      ? 0
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css`
            padding: 6px 0;
            visibility: visible;
            width: 100%;
          `,
        )}
`;

const ButtonStyle = styled.div<ServiceCardProps>`
  align-items: center;
  color: var(--theme-background-contrast-text-color);
  display: flex;
  line-height: 16px;
  justify-content: center;
  transition: color 0.3s;
  visibility: visible;
  z-index: 2;

  ${(props) =>
    props.forceSmall
      ? 0
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css`
            left: calc(15% + 30px);
            position: relative;
          `,
        )}
`;

const DetailsButton = styled.button`
  border: none;
  display: flex;
  margin-left: 5px;
  align-items: center;
  font-weight: 700;
  font-size: 12px;
  line-height: 16px;
  text-align: center;
  text-decoration: underline;
  transition: color 0.3s;
  padding: 0 0 3px;
`;

const MobileCta = styled.div<ServiceCardProps>`
  order: 4;
  display: block;
  margin: 16px 30px;
  width: 100%;

  ${(props) =>
    props.forceSmall
      ? 0
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css`
            display: none;
          `,
        )}
`;

const DesktopCta = styled.div<ServiceCardProps>`
  display: none;

  ${(props) =>
    props.forceSmall
      ? 0
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css`
            display: block;
          `,
        )}
`;

const DetailSpecs = styled.div<ServiceCardProps>`
  background: var(--theme-color-background);
  visibility: ${({ visibilty }: ServiceCardProps) => visibilty};
  display: flex;
  flex-direction: column;
  animation: identifier 0.5s ease-in-out;
  animation-iteration-count: 1;
  animation-play-state: ${({ animated }: ServiceCardProps) => animated};
  animation-direction: ${({ direction }: ServiceCardProps) => direction};
  order: 4;
  width: 100%;
  padding: 0 30px;
  max-height: ${({ visibilty }: ServiceCardProps) => (visibilty === 'visible' ? '10000px' : '0')};
  overflow: hidden;

  @keyframes identifier {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  ${(props) =>
    props.forceSmall
      ? 0
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css`
            left: calc(15% + 30px);
            padding: 0 30px 0 0;
            position: relative;
            width: calc(100% - 15% - 60px);
          `,
        )}
`;

const DetailSpecsOffer = styled.div<ServiceCardProps>`
  display: flex;
  flex-direction: column;

  ${(props) =>
    props.forceSmall
      ? 0
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css`
            display: none;
          `,
        )}
`;

const DetailTitle = styled.div<ServiceCardProps>`
  color: var(--theme-color-primary);
  font-family: var(--theme-typography-primary-font);
  font-weight: 700;
  font-size: 22px;
  line-height: 24px;
  margin-top: 15px;

  ${(props) =>
    props.forceSmall
      ? 0
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css`
            margin-bottom: 15px;
          `,
        )}
`;

const Spec = styled.div<ServiceCardProps>`
  align-items: normal;
  border-bottom: 1px solid #dddddd;
  display: flex;
  flex-direction: column;
  padding: 10px 0;
  position: relative;
  width: 100%;

  &:last-of-type {
    margin-bottom: 15px;
  }

  ${(props) =>
    props.forceSmall
      ? 0
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css`
            flex-direction: initial;
            align-items: center;
          `,
        )}
`;

const SpecLabel = styled.p<ServiceCardProps>`
  color: var(--theme-background-contrast-text-color);
  font-size: 12px;
  margin: 0 0 1px;
  opacity: 0.7;
  width: 100%;

  ${(props) =>
    props.forceSmall
      ? 0
      : mq.gte(
          DEVICE_SIZE.LARGE,
          css`
            width: 30%;
          `,
        )}
`;

const SpecValue = styled.p<ServiceCardProps>`
  font-size: 16px;
  font-weight: 700;
  margin: 2px 0 0;
`;

const Ribbon = styled.div`
  align-items: center;
  background: var(--card-border);
  border-radius: 13px 0;
  display: flex;
  height: 35px;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
  left: -1px;
  position: absolute;
  top: -1px;
  width: 35px;
  color: white;

  &.mod-dark {
    background: var(--theme-palette-grey-blue);
    left: -3px;
    top: -3px;
  }
`;

const NetworkImage = styled.div`
  height: auto;
  display: flex;
`;

type Props = {
  forceSmall: boolean;
  position?: string;
  top?: boolean;
  ribbon?: string;
  genericItem: GenericItem;
  eventCta?: (
    shopName?: string,
    price?: string,
    position?: string,
    itemId?: number,
    variantId?: number,
    title?: string,
  ) => void;
};

const excludTopSpec = ['data-envelope', 'mobile-network-used', 'technology', 'download-speed', 'operator'];

const PlanAlternativeCardShow = ({ forceSmall, position, top, ribbon, genericItem, eventCta }: Props) => {
  const [showDetails, setShowDetails] = useState(false);
  const [isHover, setIsHover] = useState(false);
  const handleClick = () => {
    setShowDetails(!showDetails);
  };
  const t = useTranslation();
  const ImageComponent = useImageComponent();
  const LinkComponent = useLinkComponent();

  const ctaLink = useMemo(
    () => ({
      href: genericItem?.price?.link ?? '',
      openInNewWindow: true,
      rel: ['nofollow'],
      event: {
        type: 'click_comparatif',
        seller: genericItem?.price?.shop?.name,
        position: position ?? '',
        price: genericItem?.price?.price,
        product: genericItem?.title,
      },
    }),
    [genericItem, position],
  );

  const productLink = useMemo(
    () => ({
      href: genericItem?.price?.link ?? null,
    }),
    [genericItem.price],
  );

  const imageProps = useMemo(
    () => ({
      imageId: genericItem?.imageId,
      alt: genericItem?.title,
      mobileWidth: 82,
      mobileHeight: 82,
      desktopWidth: 82,
      desktopHeight: 82,
    }),
    [genericItem],
  );

  const price = genericItem?.price?.price ?? '';

  const topDatasheet = useMemo(() => getTopSpecsFromDataSheet(genericItem?.dataSheet), [genericItem.dataSheet]);

  const dataNetwork = genericItem?.dataSheet?.groups?.find(
    (attribute: GenericItemDataSheetGroups) => attribute.shortSlug === 'data-network',
  );

  const storage = dataNetwork?.attributes?.find(
    (attribute: GenericItemDataSheetAttributes) => attribute.shortSlug === 'data-envelope',
  );
  const networkType = dataNetwork?.attributes?.find(
    (attribute: GenericItemDataSheetAttributes) => attribute.shortSlug === 'telecom-standards',
  );

  const network = dataNetwork?.attributes?.find(
    (attribute: GenericItemDataSheetAttributes) => attribute.shortSlug === 'mobile-network-used',
  );

  const internet = genericItem?.dataSheet?.groups?.find(
    (attribute: GenericItemDataSheetGroups) => attribute.shortSlug === 'internet',
  );

  const technology = internet?.attributes?.find(
    (attribute: GenericItemDataSheetAttributes) => attribute.shortSlug === 'technology',
  );

  const operator = internet?.attributes?.find(
    (attribute: GenericItemDataSheetAttributes) => attribute.shortSlug === 'operator',
  );

  const downloadSpeed = internet?.attributes?.find(
    (attribute: GenericItemDataSheetAttributes) => attribute.shortSlug === 'download-speed',
  );

  let networkValue = network ? network?.value : null;
  if (networkValue === null) {
    networkValue = operator ? operator?.value : null;
  }

  return (
    <Card
      data-testid={`plan-alternative-${genericItem?.id}`}
      {...ctaLink}
      onClick={() =>
        eventCta(
          genericItem?.price?.shop?.name,
          genericItem?.price?.price,
          null,
          parseInt(genericItem.item.id, 10),
          parseInt(genericItem.id, 10),
          genericItem?.title,
        )
      }
      forceSmall={forceSmall}
      className={`${top ? 'mod-top' : ''} ${isHover ? 'mod-hover' : ''}`}
    >
      <CardClickable
        forceSmall={forceSmall}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
      >
        <LinkComponent {...ctaLink} />
      </CardClickable>
      {ribbon ? <Ribbon className={top ? 'mod-dark' : null}>{ribbon}</Ribbon> : null}
      {genericItem?.price?.expirationDate ? (
        <BadgeBox forceSmall={forceSmall}>
          <Badge text={genericItem?.price?.expirationDate} />
        </BadgeBox>
      ) : null}
      <LogoArea forceSmall={forceSmall}>
        <ImageComponent {...imageProps} />
      </LogoArea>
      <LeftPart forceSmall={forceSmall}>
        <LinkComponent {...productLink}>
          <Title forceSmall={forceSmall}>{genericItem?.title}</Title>
        </LinkComponent>
        {topDatasheet?.groups?.map((group: GenericItemDataSheetGroups, indexG: number) =>
          group.attributes?.map((spec: GenericItemDataSheetAttributes, index: number) =>
            excludTopSpec.includes(spec.shortSlug) ? null : (
              <TopSpec key={`${indexG}-${index}`} forceSmall={forceSmall}>
                <TopSpecIcon symbol={spec.iconSlug} />
                {spec.value === 'Oui' ? <DetailText>{spec.name}</DetailText> : <DetailText>{spec.value}</DetailText>}
              </TopSpec>
            ),
          ),
        )}
      </LeftPart>
      <CenterPart forceSmall={forceSmall}>
        <FirstVerticalLine forceSmall={forceSmall} />
        <InternetDataBox forceSmall={forceSmall}>
          {storage ? (
            <InternetDataText forceSmall={forceSmall}>{storage?.value ?? ''}</InternetDataText>
          ) : (
            <>
              <InternetDataLabel>{t('design.onlineServiceCard.until')}</InternetDataLabel>
              <InternetDataText forceSmall={forceSmall}>{downloadSpeed?.value ?? ''}</InternetDataText>
            </>
          )}
          <NetworkWithIconArea forceSmall={forceSmall}>
            {networkType ? <span>Réseau {networkType?.value}</span> : <span>{technology?.value}</span>}
            <NetworkImage>{networkValue && <NetworkIcons networkSlug={networkValue} />}</NetworkImage>
          </NetworkWithIconArea>
        </InternetDataBox>
      </CenterPart>
      <RightPart forceSmall={forceSmall}>
        <SecondVerticalLine forceSmall={forceSmall} />
        <PriceText forceSmall={forceSmall}>{price}</PriceText>
        {genericItem?.price?.specificConditions && (
          <DiscountPeriod forceSmall={forceSmall}>{genericItem?.price?.specificConditions}</DiscountPeriod>
        )}
        <DesktopCta forceSmall={forceSmall}>
          <Cta {...ctaLink}>{t('design.onlineServiceCard.cta')} ↗</Cta>
        </DesktopCta>
      </RightPart>
      <GradiantBox onClick={handleClick} forceSmall={forceSmall}>
        <ButtonStyle forceSmall={forceSmall}>
          <MaterialSymbol symbol="unfold_more" fill={1} weight={800} size={16} />
          <DetailsButton>{t('design.planAlternative.moreDetails')}</DetailsButton>
        </ButtonStyle>
      </GradiantBox>
      <MobileCta forceSmall={forceSmall}>
        <Cta {...ctaLink}>{t('design.onlineServiceCard.cta')} ↗</Cta>
      </MobileCta>
      <DetailSpecs
        visibilty={showDetails ? 'visible' : 'hidden'}
        animated={showDetails ? 'running' : 'paused'}
        direction={showDetails ? 'normal' : 'reversed'}
        forceSmall={forceSmall}
      >
        <DetailSpecsOffer forceSmall={forceSmall}>
          {topDatasheet?.groups?.map((group: GenericItemDataSheetGroups) => (
            <React.Fragment key={group.name}>
              <DetailTitle forceSmall={forceSmall}>Offre</DetailTitle>
              {group?.attributes?.map((spec: GenericItemDataSheetAttributes, index: number) => (
                <Spec key={index} forceSmall={forceSmall}>
                  <SpecLabel forceSmall={forceSmall}>{spec.name}</SpecLabel>
                  <SpecValue>{spec.value}</SpecValue>
                </Spec>
              ))}
            </React.Fragment>
          ))}
        </DetailSpecsOffer>
        {genericItem?.dataSheet?.groups?.map((group: GenericItemDataSheetGroups) => (
          <React.Fragment key={group.name}>
            <DetailTitle forceSmall={forceSmall}>{group.name}</DetailTitle>
            {group?.attributes?.map((spec: GenericItemDataSheetAttributes, index: number) => (
              <Spec key={index} forceSmall={forceSmall}>
                <SpecLabel forceSmall={forceSmall}>{spec.name}</SpecLabel>
                <SpecValue>{spec.value}</SpecValue>
              </Spec>
            ))}
          </React.Fragment>
        ))}
      </DetailSpecs>
    </Card>
  );
};

const PlanAlternativeCard = (props: Props) => {
  const { genericItem } = props;

  if (!genericItem?.id) {
    return null;
  }

  return <PlanAlternativeCardShow {...props} />;
};

export default PlanAlternativeCard;
